import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { CtaButtonComponent } from '../../../../../shared/components/ui/cta-button/cta-button.component';
import {
  PatrolRobloxApiService,
  PatrolRobloxExperience,
  PatrolRobloxStudioScriptEndpoints,
} from '../../../services/patrol-roblox-api.service';
import {
  parseRobloxStudioBridgeFiles,
  RobloxStudioBridgeFile,
} from '../../../utils/patrol-roblox-studio-bridge.util';

@Component({
  selector: 'app-patrol-roblox-studio-bridge',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, CtaButtonComponent],
  templateUrl: './patrol-roblox-studio-bridge.component.html',
  styleUrl: './patrol-roblox-studio-bridge.component.scss',
})
export class PatrolRobloxStudioBridgeComponent implements OnInit {
  #api = inject(PatrolRobloxApiService);

  readonly experience = input.required<PatrolRobloxExperience>();

  readonly channelName = signal('RBXGeneral');
  readonly warnMuteSeconds = signal(300);
  readonly blockOnDecision = signal(true);
  readonly moderationTimeoutSeconds = signal(4);
  readonly failClosedOnOutage = signal(false);
  readonly openCloudApiKey = signal('');
  readonly copiedScriptFileId = signal<'server' | 'client' | null>(null);
  readonly copiedIngestSecret = signal(false);
  readonly activeFileId = signal<'server' | 'client'>('server');
  readonly generatedScript = signal('');
  readonly scriptEndpoints = signal<PatrolRobloxStudioScriptEndpoints | null>(
    null,
  );
  readonly isGenerating = signal(false);
  readonly isGeneratingSecret = signal(false);
  readonly isSavingOpenCloudKey = signal(false);
  readonly generateError = signal<string | null>(null);
  readonly credentialsError = signal<string | null>(null);
  readonly hasIngestSecret = signal(false);
  readonly hasOpenCloudApiKey = signal(false);
  readonly replacingOpenCloudApiKey = signal(false);
  readonly ingestSecretShownOnce = signal<string | null>(null);

  readonly showOpenCloudKeyForm = computed(
    () => !this.hasOpenCloudApiKey() || this.replacingOpenCloudApiKey(),
  );

  readonly canGenerate = computed(() => {
    const universeId = this.experience().communityId?.trim();
    return Boolean(
      this.hasIngestSecret() && universeId && this.channelName().trim(),
    );
  });

  readonly scriptFiles = computed(() =>
    parseRobloxStudioBridgeFiles(this.generatedScript()),
  );

  readonly activeFile = computed(
    () =>
      this.scriptFiles().find((file) => file.id === this.activeFileId()) ??
      this.scriptFiles()[0] ??
      null,
  );

  readonly activeLineNumbers = computed(() => {
    const lineCount = (this.activeFile()?.content.split('\n').length ?? 0) || 1;
    return Array.from({ length: lineCount }, (_, index) => index + 1);
  });

  ngOnInit(): void {
    void this.refreshCredentials();
  }

  selectFile(file: RobloxStudioBridgeFile): void {
    this.activeFileId.set(file.id);
  }

  async refreshCredentials(): Promise<void> {
    try {
      const status = await this.#api.getCredentials(this.experience().registryId);
      this.hasIngestSecret.set(status.hasIngestSecret);
      this.hasOpenCloudApiKey.set(status.hasOpenCloudApiKey);
      this.credentialsError.set(null);
    } catch {
      this.credentialsError.set('credentials_load_failed');
    }
  }

  async generateIngestSecret(): Promise<void> {
    if (this.isGeneratingSecret()) {
      return;
    }
    this.isGeneratingSecret.set(true);
    this.credentialsError.set(null);
    try {
      const result = await this.#api.generateIngestSecret(
        this.experience().registryId,
      );
      this.ingestSecretShownOnce.set(result.ingestSecret);
      this.hasIngestSecret.set(true);
    } catch {
      this.credentialsError.set('ingest_secret_failed');
    } finally {
      this.isGeneratingSecret.set(false);
    }
  }

  async saveOpenCloudApiKey(): Promise<void> {
    const apiKey = this.openCloudApiKey().trim();
    if (!apiKey || this.isSavingOpenCloudKey()) {
      return;
    }
    this.isSavingOpenCloudKey.set(true);
    this.credentialsError.set(null);
    try {
      await this.#api.saveOpenCloudApiKey(this.experience().registryId, apiKey);
      this.openCloudApiKey.set('');
      this.hasOpenCloudApiKey.set(true);
      this.replacingOpenCloudApiKey.set(false);
    } catch {
      this.credentialsError.set('open_cloud_key_failed');
    } finally {
      this.isSavingOpenCloudKey.set(false);
    }
  }

  async generateScript(): Promise<void> {
    if (!this.canGenerate() || this.isGenerating()) {
      return;
    }

    this.isGenerating.set(true);
    this.generateError.set(null);
    try {
      const response = await this.#api.generateStudioScript(
        this.experience().registryId,
        {
          channelName: this.channelName().trim() || 'RBXGeneral',
          warnMuteSeconds: this.warnMuteSeconds(),
          blockOnDecision: this.blockOnDecision(),
          moderationTimeoutSeconds: this.moderationTimeoutSeconds(),
          failClosedOnOutage: this.failClosedOnOutage(),
        },
      );
      this.generatedScript.set(response.script);
      this.scriptEndpoints.set(response.endpoints);
      this.activeFileId.set('server');
      this.copiedScriptFileId.set(null);
    } catch {
      this.generatedScript.set('');
      this.scriptEndpoints.set(null);
      this.generateError.set('generate_failed');
    } finally {
      this.isGenerating.set(false);
    }
  }

  async copyScriptFile(file: RobloxStudioBridgeFile): Promise<void> {
    if (!file.content) {
      return;
    }
    try {
      await navigator.clipboard.writeText(file.content);
      this.copiedScriptFileId.set(file.id);
      setTimeout(() => {
        if (this.copiedScriptFileId() === file.id) {
          this.copiedScriptFileId.set(null);
        }
      }, 2000);
    } catch {
      this.copiedScriptFileId.set(null);
    }
  }

  startReplacingOpenCloudApiKey(): void {
    this.replacingOpenCloudApiKey.set(true);
    this.openCloudApiKey.set('');
    this.credentialsError.set(null);
  }

  cancelReplacingOpenCloudApiKey(): void {
    this.replacingOpenCloudApiKey.set(false);
    this.openCloudApiKey.set('');
    this.credentialsError.set(null);
  }

  async copyIngestSecret(): Promise<void> {
    const secret = this.ingestSecretShownOnce();
    if (!secret) {
      return;
    }
    try {
      await navigator.clipboard.writeText(secret);
      this.copiedIngestSecret.set(true);
      setTimeout(() => this.copiedIngestSecret.set(false), 2000);
    } catch {
      this.copiedIngestSecret.set(false);
    }
  }
}
